import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  makeStyles, Button, IconButton, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, Switch, FormControlLabel,
  Select, MenuItem, InputLabel, FormControl, Tooltip, Typography,
  InputAdornment, LinearProgress, Menu,
} from "@material-ui/core";
import {
  Add, Edit, Delete, Search, GetApp, Publish, ArrowDropDown,
} from "@material-ui/icons";
import * as XLSX from "xlsx";
import api from "../../services/api";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  toolbar: { display: "flex", gap: theme.spacing(1), marginBottom: theme.spacing(2), flexWrap: "wrap", alignItems: "center" },
  paper: { overflowX: "auto" },
  table: { minWidth: 700 },
  active: { color: "#4caf50", fontWeight: "bold" },
  inactive: { color: "#f44336" },
  spacer: { flex: 1 },
}));

const UNITS = ["un", "kg", "m", "m2", "hora", "servico", "outro"];
const UNIT_LABELS = { un: "Unidade", kg: "Kg", m: "Metro", m2: "Metro²", hora: "Hora", servico: "Serviço", outro: "Outro" };

const CSV_HEADERS = ["codigo", "nome", "descricao", "categoria", "unidade", "preco", "ativo"];
const CSV_TEMPLATE = CSV_HEADERS.join(";") + "\n" +
  "PROD001;Produto Exemplo;Descrição opcional;Categoria;un;99.90;sim\n" +
  "SERV001;Serviço Exemplo;;TI;hora;150.00;sim";

const exportXLSX = (rows, headers, filename) => {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const colWidths = headers.map((h, i) => ({
    wch: Math.max(h.length, ...rows.map((r) => String(r[i] ?? "").length), 10),
  }));
  ws["!cols"] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Dados");
  XLSX.writeFile(wb, filename);
};

const empty = { name: "", code: "", description: "", category: "", unit: "un", price: "", active: true };

const parseCSV = (text) => {
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(";").map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((line) => {
    const cols = line.split(";");
    const row = {};
    headers.forEach((h, i) => { row[h] = (cols[i] || "").trim(); });
    return row;
  });
};

export default function ProposalProducts() {
  const classes = useStyles();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: "" });
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/proposal-products", { params: { searchParam: search } });
      setProducts(data);
    } catch { toast.error("Erro ao carregar produtos"); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleOpen = (product = null) => {
    setEditing(product);
    setForm(product ? { ...product } : empty);
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0 };
      if (editing) {
        await api.put(`/proposal-products/${editing.id}`, payload);
        toast.success("Produto atualizado");
      } else {
        await api.post("/proposal-products", payload);
        toast.success("Produto criado");
      }
      setOpen(false);
      load();
    } catch { toast.error("Erro ao salvar produto"); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/proposal-products/${deleteModal.id}`);
      toast.success("Produto deletado");
      setDeleteModal({ open: false, id: null, name: "" });
      load();
    } catch { toast.error("Erro ao deletar produto"); }
  };

  const handleExport = () => {
    setMenuAnchor(null);
    if (products.length === 0) { toast.info("Nenhum produto para exportar"); return; }
    const headers = ["Código", "Nome", "Descrição", "Categoria", "Unidade", "Preço (R$)", "Ativo"];
    const rows = products.map((p) => [
      p.code || "",
      p.name,
      p.description || "",
      p.category || "",
      UNIT_LABELS[p.unit] || p.unit,
      Number(p.price) || 0,
      p.active ? "Sim" : "Não",
    ]);
    exportXLSX(rows, headers, "produtos.xlsx");
    toast.success(`${products.length} produto(s) exportado(s)`);
  };

  const handleDownloadTemplate = () => {
    setMenuAnchor(null);
    const headers = ["codigo", "nome", "descricao", "categoria", "unidade", "preco", "ativo"];
    const rows = [
      ["PROD001", "Produto Exemplo", "Descrição opcional", "Categoria", "un", 99.90, "sim"],
      ["SERV001", "Serviço Exemplo", "", "TI", "hora", 150.00, "sim"],
    ];
    exportXLSX(rows, headers, "modelo_produtos.xlsx");
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const text = await file.text();
    const rows = parseCSV(text);
    if (rows.length === 0) { toast.error("Arquivo vazio ou formato inválido"); return; }

    const unitMap = { unidade: "un", kg: "kg", metro: "m", "metro²": "m2", hora: "hora", serviço: "servico", servico: "servico", outro: "outro" };

    setImporting(true);
    setImportProgress(0);
    let ok = 0;
    let fail = 0;

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const nome = r["nome"] || r["name"] || "";
      if (!nome) { fail++; continue; }
      const unitRaw = (r["unidade"] || r["unit"] || "un").toLowerCase();
      const unit = UNITS.includes(unitRaw) ? unitRaw : (unitMap[unitRaw] || "un");
      const ativoRaw = (r["ativo"] || r["active"] || "sim").toLowerCase();
      const active = ativoRaw === "sim" || ativoRaw === "true" || ativoRaw === "1" || ativoRaw === "s";
      try {
        await api.post("/proposal-products", {
          name: nome,
          code: r["codigo"] || r["code"] || "",
          description: r["descricao"] || r["description"] || "",
          category: r["categoria"] || r["category"] || "",
          unit,
          price: parseFloat((r["preco"] || r["price"] || "0").replace(",", ".")) || 0,
          active,
        });
        ok++;
      } catch { fail++; }
      setImportProgress(Math.round(((i + 1) / rows.length) * 100));
    }

    setImporting(false);
    setImportProgress(0);
    load();
    if (fail === 0) toast.success(`${ok} produto(s) importado(s) com sucesso`);
    else toast.warning(`${ok} importado(s), ${fail} com erro`);
  };

  const fmt = (v) => Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>Produtos / Serviços</Typography>

      {importing && (
        <div style={{ marginBottom: 12 }}>
          <Typography variant="caption">Importando... {importProgress}%</Typography>
          <LinearProgress variant="determinate" value={importProgress} />
        </div>
      )}

      <div className={classes.toolbar}>
        <TextField
          size="small" variant="outlined" placeholder="Buscar..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          style={{ minWidth: 250 }}
        />
        <div className={classes.spacer} />

        {/* Import button */}
        <input
          ref={fileInputRef} type="file" accept=".csv"
          style={{ display: "none" }} onChange={handleImportFile}
        />
        <Button
          variant="outlined" startIcon={<Publish />}
          onClick={() => fileInputRef.current.click()}
          disabled={importing}
        >
          Importar CSV
        </Button>

        {/* Export menu */}
        <Button
          variant="outlined" startIcon={<GetApp />} endIcon={<ArrowDropDown />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          Exportar
        </Button>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={handleExport}>
            <GetApp fontSize="small" style={{ marginRight: 8 }} /> Exportar lista atual (.xlsx)
          </MenuItem>
          <MenuItem onClick={handleDownloadTemplate}>
            <GetApp fontSize="small" style={{ marginRight: 8 }} /> Baixar modelo de importação (.xlsx)
          </MenuItem>
        </Menu>

        <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => handleOpen()}>
          Novo Produto
        </Button>
      </div>

      <Paper className={classes.paper}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Unidade</TableCell>
              <TableCell align="right">Preço</TableCell>
              <TableCell align="center">Ativo</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>{p.code || "—"}</TableCell>
                <TableCell>{p.name}</TableCell>
                <TableCell>{p.category || "—"}</TableCell>
                <TableCell>{UNIT_LABELS[p.unit] || p.unit}</TableCell>
                <TableCell align="right">{fmt(p.price)}</TableCell>
                <TableCell align="center">
                  <span className={p.active ? classes.active : classes.inactive}>
                    {p.active ? "Sim" : "Não"}
                  </span>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => handleOpen(p)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Excluir"><IconButton size="small" onClick={() => setDeleteModal({ open: true, id: p.id, name: p.name })}><Delete fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {products.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center">Nenhum produto encontrado</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Editar Produto" : "Novo Produto"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={8}>
              <TextField fullWidth label="Nome *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} variant="outlined" size="small" />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Código" value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} variant="outlined" size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Categoria" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} variant="outlined" size="small" />
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Unidade</InputLabel>
                <Select label="Unidade" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                  {UNITS.map((u) => <MenuItem key={u} value={u}>{UNIT_LABELS[u]}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth label="Preço (R$)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} variant="outlined" size="small" type="number" inputProps={{ min: 0, step: 0.01 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descrição" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} variant="outlined" size="small" multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} color="primary" />}
                label="Produto ativo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} color="primary" variant="contained" disabled={!form.name}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Excluir Produto"
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null, name: "" })}
        onConfirm={handleDelete}
      >
        Deseja excluir o produto <strong>{deleteModal.name}</strong>? Esta ação não pode ser desfeita.
      </ConfirmationModal>
    </div>
  );
}
